import type { ContentBlock } from '@/types/newsService';
import { cn } from '@/lib/cn';

/**
 * Renders the qnews "portable text" block array (paragraph, heading, image,
 * quote, list, divider, callout, code, embed). Falls back to a paragraph for
 * unknown types so we never blank-page on a new block kind.
 */
export function ArticleBlocks({ blocks }: { blocks: readonly ContentBlock[] }) {
  return (
    <div className="flex flex-col gap-4">
      {blocks.map((block) => (
        <Block key={block._key} block={block} />
      ))}
    </div>
  );
}

function Block({ block }: { block: ContentBlock }) {
  switch (block.type) {
    case 'heading': {
      const level = Math.min(Math.max(block.level ?? 2, 1), 4);
      const className = cn(
        'font-bold text-fg',
        level === 1 && 'text-2xl',
        level === 2 && 'text-xl',
        level === 3 && 'text-lg',
        level === 4 && 'text-base',
      );
      const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4';
      return <Tag className={className}>{block.text}</Tag>;
    }
    case 'paragraph':
      return <p className="text-base leading-relaxed text-fg">{block.text}</p>;
    case 'image':
      return (
        <figure className="-mx-4">
          {block.imageUrl && (
            <img
              src={block.imageUrl}
              alt={block.alt ?? ''}
              loading="lazy"
              className="w-full"
            />
          )}
          {(block.caption || block.attribution) && (
            <figcaption className="mt-1 px-4 text-xs text-fg-muted">
              {block.caption}
              {block.caption && block.attribution && ' · '}
              {block.attribution}
            </figcaption>
          )}
        </figure>
      );
    case 'quote':
      return (
        <blockquote className="border-l-2 border-brand pl-4 text-base italic text-fg-muted">
          {block.text}
          {block.attribution && (
            <cite className="mt-1 block text-xs not-italic text-fg-muted">
              — {block.attribution}
            </cite>
          )}
        </blockquote>
      );
    case 'list':
      return (
        <ul className="list-disc pl-5 text-base leading-relaxed text-fg">
          {(block.items ?? []).map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      );
    case 'callout':
      return (
        <aside className="rounded-xl border border-brand/30 bg-brand/5 p-4 text-sm text-fg">
          {block.text}
        </aside>
      );
    case 'divider':
      return <hr className="border-border" />;
    case 'code':
      return (
        <pre className="overflow-x-auto rounded-lg bg-bg-tertiary p-3 text-xs text-fg">
          <code>{block.code}</code>
        </pre>
      );
    case 'embed':
      return block.embedUrl ? (
        <a
          href={block.embedUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="block rounded-xl bg-bg-secondary p-3 text-sm text-link"
        >
          {block.embedUrl}
        </a>
      ) : null;
    default:
      return block.text ? (
        <p className="text-base leading-relaxed text-fg">{block.text}</p>
      ) : null;
  }
}
